import { useEffect, useState } from "react";

import { createColumnHelper } from "@tanstack/react-table";
import { ArrowPathIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import { Table } from "../../components/base/Table/Table";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../../components/shared/Notifications/useNotifications";
import useTemplateMutations from "../../data/Template/useTemplateMutations";
import { TemplateQueryKeys } from "../../data/Template/keys";
import { ITemplate, TemplateType, templateTypeLabels, templateTypeOptions } from "../../models/Template/Template";

const colHelper = createColumnHelper<ITemplate>();

const inputClasses = "w-full py-1 px-1 mt-1 text-sm text-gray-900 border border-border";

// The base templates every customer gets. Customers can't create templates, only make their own copy
// of one of these in their Template Editor, so this page is where a new kind of template starts.
export default function TemplatesPage() {
  const { setInfo } = usePageNameContext();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Template");
  const { addNotification } = useNotification();
  const { syncTemplates, create, update, remove } = useTemplateMutations();

  const [createType, setCreateType] = useState<TemplateType>(TemplateType.Email);
  const [createTitle, setCreateTitle] = useState("");
  const [createBody, setCreateBody] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ITemplate | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState<ITemplate | null>(null);

  useEffect(() => {
    setInfo({
      name: "Templates",
      desc: "The base templates every customer gets. Changes reach customers at the nightly sync, or straight away with Sync now",
      pageTitle: "Templates",
    });
  }, []);

  // The API says why a save was refused (a duplicate title, a type change), so show that rather
  // than a generic failure.
  const showSaveError = (error: Error) => {
    addNotification({ variant: "error", primaryText: error?.message || "Failed to save template" });
  };

  const handleCreateTemplate = () => {
    const title = createTitle.trim();
    if (!title) return;

    create.mutate(
      { type: createType, title, body: createBody },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setCreateTitle("");
          setCreateBody("");
        },
        onError: showSaveError,
      },
    );
  };

  const handleOpenEditModal = (template: ITemplate) => {
    setEditingTemplate(template);
    setEditTitle(template.title);
    setEditBody(template.body ?? "");
    setEditOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editingTemplate) return;

    const title = editTitle.trim();
    if (!title) return;

    update.mutate(
      { ...editingTemplate, title, body: editBody },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setEditOpen(false);
          setEditingTemplate(null);
        },
        onError: showSaveError,
      },
    );
  };

  const handleDeleteTemplate = (template: ITemplate) => {
    setDeletingTemplate(template);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!deletingTemplate) return;

    remove.mutate(deletingTemplate.id, {
      ...deleteHandlers,
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setDeleteOpen(false);
        setDeletingTemplate(null);
      },
    });
  };

  const cols = [
    colHelper.accessor("type", {
      header: "Type",
      cell: (info) => templateTypeLabels[info.row.original.type] ?? info.row.original.type,
    }),
    colHelper.accessor("title", { header: "Title" }),
    colHelper.accessor("body", {
      header: "Body",
      cell: (info) => {
        const body = info.row.original.body ?? "";
        return (
          <span className="text-gray-500 font-mono text-xs" title={body}>
            {body.length > 80 ? `${body.slice(0, 80)}…` : body}
          </span>
        );
      },
    }),
    colHelper.display({
      header: "Last changed",
      cell: (info) => {
        const changed = info.row.original.updated ?? info.row.original.created;
        return changed ? new Date(changed).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "";
      },
    }),
    colHelper.display({
      header: "Actions",
      cell: (info) => {
        const template = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="p-1 rounded hover:text-blue-700"
              onClick={() => handleOpenEditModal(template)}
              title="Edit template"
            >
              <PencilIcon className="w-5 h-5 opacity-70 hover:opacity-100" />
            </button>
            <button
              type="button"
              className="p-1 rounded hover:text-red-700"
              onClick={() => handleDeleteTemplate(template)}
              disabled={remove.isPending}
              title="Delete template"
            >
              <TrashIcon className="w-5 h-5 opacity-70 hover:opacity-100" />
            </button>
          </div>
        );
      },
    }),
  ];

  return (
    <div className="flex flex-col w-full h-full gap-6">
      <ConfirmModal
        open={deleteOpen}
        setOpen={(open: boolean) => {
          setDeleteOpen(open);
          if (!open) {
            setDeletingTemplate(null);
          }
        }}
        title={"Delete Template?"}
        message={`Delete "${deletingTemplate?.title ?? ""}"?\nThe next sync removes it from every customer that hasn't made their own copy of it. Customers with their own copy keep that.`}
        confirmButtonText={"Delete"}
        onConfirm={handleConfirmDelete}
        isLoading={remove.isPending}
      />

      <Modal open={editOpen} setOpen={setEditOpen} width="sm:max-w-3xl">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl">Edit Template</h1>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">Type</label>
            {/* Not editable: customers' own copies keep the type they were made with. */}
            <p className="mt-1 text-sm text-gray-900">
              {editingTemplate ? templateTypeLabels[editingTemplate.type] : ""}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Title</label>
            <input
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className={inputClasses}
              placeholder="Template title"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Body</label>
            <textarea
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              className={`${inputClasses} font-mono`}
              rows={16}
            />
          </div>
          <p className="text-xs text-gray-500">
            Customers who made their own copy keep theirs. Everyone else gets this at the next sync.
          </p>
        </div>
        <div className="mt-4 flex gap-2">
          <ButtonInput
            label="Cancel"
            isSubmit={false}
            classes="!bg-white !text-gray-700 !border !border-gray-300 hover:!bg-gray-50"
            onClick={() => setEditOpen(false)}
          />
          <ButtonInput
            classes=""
            label="Save"
            isSubmit={false}
            onClick={handleSaveEdit}
            disabled={update.isPending}
          />
        </div>
      </Modal>

      <div className="bg-white border border-gray-200 rounded p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          Templates reach customers at the nightly sync (2am). Sync now to send changes to every customer straight away.
        </p>
        <button
          type="button"
          onClick={() => syncTemplates.mutate([])}
          disabled={syncTemplates.isPending}
          className="inline-flex shrink-0 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          <ArrowPathIcon className={`w-4 h-4 ${syncTemplates.isPending ? "animate-spin" : ""}`} />
          Sync now
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded p-4">
        <h3 className="text-lg font-semibold mb-3">Create Template</h3>
        <div className="grid grid-cols-1 gap-3">
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2">
            <div>
              <label className="text-sm font-medium text-gray-500">Type</label>
              <select
                value={createType}
                onChange={(e) => setCreateType(parseInt(e.target.value, 10) as TemplateType)}
                className={`${inputClasses} bg-white`}
              >
                {templateTypeOptions.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Title</label>
              <input
                value={createTitle}
                onChange={(e) => setCreateTitle(e.target.value)}
                className={inputClasses}
                placeholder="What customers see it called in their Template Editor"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Body</label>
            <textarea
              value={createBody}
              onChange={(e) => setCreateBody(e.target.value)}
              className={`${inputClasses} font-mono`}
              rows={6}
              placeholder="Template text, with merge tags such as {{Grade}}"
            />
          </div>
          <div>
            <ButtonInput
              label="Create Template"
              isSubmit={false}
              onClick={handleCreateTemplate}
              disabled={create.isPending || !createTitle.trim()}
              classes="h-fit"
            />
          </div>
        </div>
      </div>

      <Table<ITemplate> url="/template" queryKey={TemplateQueryKeys.all()} columns={cols} hideTotalCount />
    </div>
  );
}
